import { useEffect, useState } from "react";
import axios from "axios";
import { AuthImage } from "@/components/AuthImage";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FileText, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Admin: proof-of-payment files the parent attached to this invoice reference. */
export const PaymentProofsPanel = ({ reference }) => {
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/media/payment-proof/${encodeURIComponent(reference)}`, {
          withCredentials: true,
        });
        setFiles(res.data?.files || []);
      } catch (err) {
        setFiles([]);
      }
    };
    if (reference) load();
  }, [reference]);

  if (!reference || files.length === 0) return null;

  return (
    <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="drawer-payment-proofs">
      <p className="text-xs text-cyan-200 font-semibold flex items-center gap-1 mb-2">
        <Paperclip className="w-3.5 h-3.5" /> Proof of payment ({files.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {files.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setOpen(f)}
            className="w-20 h-20 rounded-md overflow-hidden border border-slate-700 hover:border-cyan-400 transition-colors"
            title={`${f.original_filename} · uploaded ${f.created_at?.slice(0, 10)}`}
            data-testid={`payment-proof-thumb-${f.id}`}
          >
            {(f.content_type || "").startsWith("image/") ? (
              <AuthImage src={`${API}/media/${f.id}/raw`} alt={f.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-800">
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
            )}
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl" data-testid="payment-proof-dialog">
          {open && (
            <div className="space-y-3">
              {(open.content_type || "").startsWith("image/") ? (
                <AuthImage
                  src={`${API}/media/${open.id}/raw`}
                  alt={open.title}
                  className="w-full max-h-[65vh] object-contain rounded-lg bg-slate-950"
                />
              ) : (
                <a
                  href={`${API}/media/${open.id}/raw?download=true&auth=${encodeURIComponent(localStorage.getItem("scc_session_token") || "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-cyan-300 underline text-sm"
                >
                  Download {open.original_filename}
                </a>
              )}
              <p className="text-xs text-slate-400">
                {open.original_filename} · uploaded {open.created_at?.slice(0, 16).replace("T", " ")}
                {open.note ? ` · "${open.note}"` : ""}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentProofsPanel;
